// Defaults & Workout rebuild — compact side-rail cards: each is a status
// chip + one line + a drawer hop (no forms here; the timeline + drawer own
// the detail, the action rail owns the writes). The Enforcer card is REAL
// now — it lists the case's engagements via the new
// GET /default-cases/{id}/engagements endpoint with registry links.

import type { ReactNode } from "react";
import { Badge } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import type { CaseArtifacts } from "./timeline";
import { timelineKey } from "./timeline";

const fmtCurrency = (n: number): string => formatCurrency(n);

interface SideRailCardsProps {
  artifacts: CaseArtifacts;
  onOpen: (key: string) => void;
}

interface RailCardProps {
  title: string;
  badge: ReactNode;
  line: string;
  openKey: string | null;
  onOpen: (key: string) => void;
  testId: string;
}

function RailCard({ title, badge, line, openKey, onOpen, testId }: RailCardProps) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-space-2">
        <span className="text-small font-bold">{title}</span>
        {badge}
      </div>
      <p className="m-0 text-caption text-gray-mid truncate">{line}</p>
    </>
  );
  if (!openKey) {
    return (
      <div
        className="border border-border-light rounded-md bg-white-pure p-space-2 flex flex-col gap-space-1"
        data-testid={testId}
      >
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(openKey)}
      className="border border-border-light rounded-md bg-white-pure p-space-2 flex flex-col gap-space-1 text-left hover:bg-white-warm w-full"
      data-testid={testId}
    >
      {inner}
    </button>
  );
}

export function SideRailCards({ artifacts, onOpen }: SideRailCardsProps) {
  const { plans, forbearance, daca, legalActions, engagements, costs, recoveries } =
    artifacts;

  const activePlan =
    plans.find((p) => p.status === "active") ?? plans[0] ?? null;
  const latestForbearance = forbearance[0] ?? null;
  const activeDaca = daca.find((d) => d.status === "active") ?? daca[0] ?? null;
  const openLegal = legalActions.filter((l) => l.status !== "closed");
  const activeEngagements = engagements.filter((e) => e.status === "active");
  const totalCosts = costs.reduce((s, c) => s + c.amount, 0);
  // Recoverable costs now sit in B3's Fee review queue until approved there.
  const pendingCosts = costs.filter(
    (c) => c.pipelineStatus === "pending_review",
  ).length;
  // Read-derived: borrower repayment collected back through the waterfall.
  const recoupedFromBorrower = costs.reduce((s, c) => s + c.recoveredAmount, 0);
  const netRecoveries = recoveries.reduce((s, r) => s + r.netAmount, 0);

  return (
    <div className="flex flex-col gap-space-2" data-testid="case-side-rail">
      <RailCard
        title="Remediation plan"
        badge={
          activePlan ? (
            <Badge status={activePlan.status === "active" ? "info" : "draft"}>
              Level {activePlan.level}
            </Badge>
          ) : (
            <Badge status="draft">None</Badge>
          )
        }
        line={
          activePlan
            ? `${activePlan.title} — ${activePlan.status}`
            : "No plan proposed yet."
        }
        openKey={activePlan ? timelineKey("remediation-plan", activePlan.id) : null}
        onOpen={onOpen}
        testId="rail-remediation"
      />
      <RailCard
        title="Forbearance"
        badge={
          latestForbearance ? (
            <Badge status={latestForbearance.status === "executed" ? "info" : "draft"}>
              {latestForbearance.status}
            </Badge>
          ) : (
            <Badge status="draft">None</Badge>
          )
        }
        line={
          latestForbearance
            ? `${latestForbearance.forbearanceType}${
                latestForbearance.forbearanceAmount !== null
                  ? ` • ${fmtCurrency(latestForbearance.forbearanceAmount)}`
                  : ""
              }`
            : "No agreement on file."
        }
        openKey={
          latestForbearance ? timelineKey("forbearance", latestForbearance.id) : null
        }
        onOpen={onOpen}
        testId="rail-forbearance"
      />
      <RailCard
        title="DACA"
        badge={
          activeDaca?.status === "active" ? (
            <Badge status="info">Active</Badge>
          ) : (
            <Badge status="draft">Inactive</Badge>
          )
        }
        line={
          activeDaca
            ? `Monthly collection ${fmtCurrency(activeDaca.monthlyCollectionAmount)}`
            : "No activation."
        }
        openKey={activeDaca ? timelineKey("daca", activeDaca.id) : null}
        onOpen={onOpen}
        testId="rail-daca"
      />
      <RailCard
        title="Legal actions"
        badge={
          openLegal.length > 0 ? (
            <Badge status="review">{openLegal.length} open</Badge>
          ) : (
            <Badge status="draft">None</Badge>
          )
        }
        line={
          openLegal.length > 0
            ? openLegal.map((l) => l.actionType).join(" • ")
            : "No legal actions initiated."
        }
        openKey={openLegal[0] ? timelineKey("legal-action", openLegal[0].id) : null}
        onOpen={onOpen}
        testId="rail-legal"
      />
      <RailCard
        title="Enforcer engagements"
        badge={
          activeEngagements.length > 0 ? (
            <Badge status="info">{activeEngagements.length} active</Badge>
          ) : (
            <Badge status="draft">None</Badge>
          )
        }
        line={
          engagements.length > 0
            ? engagements
                .map((e) => `${e.engagementType} (${e.status})`)
                .join(" • ")
            : "No engagements yet."
        }
        openKey={engagements[0] ? timelineKey("engagement", engagements[0].id) : null}
        onOpen={onOpen}
        testId="rail-enforcers"
      />
      <RailCard
        title="Costs vs recoveries"
        badge={
          pendingCosts > 0 ? (
            <Badge status="review">{pendingCosts} pending B3 review</Badge>
          ) : (
            <Badge status={netRecoveries - totalCosts >= 0 ? "finalized" : "error"}>
              {fmtCurrency(netRecoveries - totalCosts)}
            </Badge>
          )
        }
        line={`Costs ${fmtCurrency(totalCosts)} • Recouped ${fmtCurrency(recoupedFromBorrower)} • Net recoveries ${fmtCurrency(netRecoveries)}`}
        openKey={
          costs[0]
            ? timelineKey("cost", costs[0].id)
            : recoveries[0]
              ? timelineKey("recovery", recoveries[0].id)
              : null
        }
        onOpen={onOpen}
        testId="rail-ledger"
      />
    </div>
  );
}
